import { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Badge, Button, Card, InsurerLogo, Pagination, Select } from '@/components/common';
import { useAuthStore } from '@/store/auth';
import { commissionService, reconciliationService, getErrorMessage } from '@/services';
import type { ReconciliationExportFormat, ReconciliationFilters } from '@/services/commissions';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { useRematchPending, useUploadStatement } from '@/hooks/useReconciliation';
import { useFilteredReconciliationSummary, useReconciliationLines } from '@/hooks/useCommissions';
import { cn, formatCurrency, formatDate } from '@/utils';
import type {
  DifferenceReportQuery,
  StatementLine,
  StatementLineStatus,
} from '@/services/reconciliation';
import {
  describeRowError,
  uploadRowErrors,
  uploadRecheckedCount,
  uploadSkippedCount,
  uploadUpdatedCount,
} from './statementUpload';
import {
  STATEMENT_FORMAT_OPTIONS,
  STATEMENT_UPLOAD_ACCEPT,
  TONE_CLASS,
  differenceTone,
  formatSignedAmount,
  lineDifference,
  netDifference,
} from './commissionComparison';
import { StatementLineHistoryDrawer } from './StatementLineHistoryDrawer';
import { StatementDifferenceReport } from './StatementDifferenceReport';

// ─── Status helpers ────────────────────────────────────────────────────────────

const statusVariant: Record<
  StatementLineStatus,
  'success' | 'error' | 'default' | 'warning'
> = {
  reconciled: 'success',
  disputed: 'error',
  unmatched: 'default',
  pending: 'warning',
};

/**
 * The user asked for "Matched" and "Mismatched". `unmatched` is deliberately
 * NOT folded into "Mismatched": a line whose amount disagrees (disputed) is a
 * conversation with the insurer, while a line whose policy we cannot find at
 * all is a data problem at our end. They need different actions, so they keep
 * different words. The VALUES are the API's own status strings and must not
 * change with the labels.
 */
const statusLabel: Record<StatementLineStatus, string> = {
  reconciled: 'Matched',
  disputed: 'Mismatched',
  unmatched: 'No matching policy',
  pending: 'Pending',
};

/**
 * M-02. "Pending" to the broker means "the insurer paid for a policy you have
 * not issued yet". The API stores such a line as `unmatched` and flags it
 * `awaiting_policy` (it carries the broker it was uploaded under, so it is
 * matched automatically once that policy's commission is calculated). A line
 * with no broker behind it can never be auto-matched and keeps the plain
 * "No matching policy".
 */
export const AWAITING_POLICY_LABEL = 'Pending — policy not yet issued';

export function lineStatusDisplay(line: Pick<StatementLine, 'status' | 'awaiting_policy'>): {
  label: string;
  variant: 'success' | 'error' | 'default' | 'warning';
} {
  if (line.status === 'unmatched' && line.awaiting_policy) {
    return { label: AWAITING_POLICY_LABEL, variant: 'warning' };
  }
  return {
    label: statusLabel[line.status] ?? line.status,
    variant: statusVariant[line.status] ?? 'default',
  };
}

/** The raw `pending` status (a line not yet reconciled at all) is transient
 *  and not offered here; "Pending" in this list is the M-02 meaning above. */
const AWAITING_FILTER = 'awaiting_policy';

const statusFilterOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'reconciled', label: statusLabel.reconciled },
  { value: 'disputed', label: statusLabel.disputed },
  { value: AWAITING_FILTER, label: AWAITING_POLICY_LABEL },
  { value: 'unmatched', label: statusLabel.unmatched },
];

/**
 * 3b. The five KPI cards ARE the status filter — the Status dropdown is the
 * same control in another shape, and the two stay in sync because both write
 * `statusFilter`.
 *
 * `filterValue` is what a click sets (and what a second click clears back to
 * `''`); `bucket` is the key the summary serves its count and net difference
 * under. "Net Difference" has no status of its own: it is the whole statement,
 * so it selects "All Statuses" and is the card that is pressed when nothing
 * else is.
 */
type KpiBucketKey = 'reconciled' | 'disputed' | 'awaiting_policy' | 'no_matching_policy' | 'all';

const KPI_CARDS: {
  filterValue: string;
  bucket: KpiBucketKey;
  label: string;
  color: string;
  subLabel: string;
}[] = [
  {
    filterValue: 'reconciled',
    bucket: 'reconciled',
    label: statusLabel.reconciled,
    color: 'text-green-600 dark:text-green-400',
    subLabel: 'Received matches calculated',
  },
  {
    filterValue: 'disputed',
    bucket: 'disputed',
    label: statusLabel.disputed,
    color: 'text-red-600 dark:text-red-400',
    subLabel: 'Amounts disagree',
  },
  {
    filterValue: AWAITING_FILTER,
    bucket: 'awaiting_policy',
    label: 'Pending',
    color: 'text-amber-600 dark:text-amber-400',
    subLabel: 'Policy not yet issued; matched automatically',
  },
  {
    filterValue: 'unmatched',
    bucket: 'no_matching_policy',
    label: statusLabel.unmatched,
    color: 'text-gray-500 dark:text-gray-400',
    subLabel: 'No such policy in your records',
  },
  {
    filterValue: '',
    bucket: 'all',
    label: 'Net Difference',
    // The one card whose headline is money, so its colour is the difference's
    // own tone rather than a fixed one — set where it is rendered.
    color: '',
    subLabel: 'Every line in view',
  },
];

// ─── CommissionComparisonTab ──────────────────────────────────────────────────

/**
 * Commission Reconciliation — sits beside Calculations because it is the other
 * half of the same question: Calculations says what the insurer OWES, this says
 * what the insurer actually PAID, per policy.
 *
 * "Received" is the insurer's own statement line; "Calculated" is the matched
 * CommissionCalculation's total commission (the auto-approved figure on the
 * Calculations tab). The Difference column is `received - calculated`, so
 * **positive (insurer paid more) is green and negative (shortfall) is red** —
 * see `commissionComparison.ts` for why that is not the API's raw `delta`.
 */
const dateInputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500';

/** The download's file name says which dates it holds. */
export function reconciliationFileName(format: ReconciliationExportFormat, filters: ReconciliationFilters) {
  const range = [filters.statement_date_after, filters.statement_date_before].filter(Boolean).join('_');
  return `commission_reconciliation${range ? `_${range}` : ''}.${format}`;
}

export function CommissionComparisonTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  // 3c: TWO insurer selections, deliberately separate pieces of state.
  // `insurerFilter` narrows what is on screen; `uploadInsurer` says whose
  // statement the next file is. Sharing one value meant narrowing the view to
  // one company silently re-aimed the next upload at it, which posts real
  // money lines against the wrong insurer.
  const [insurerFilter, setInsurerFilter] = useState('');
  const [uploadInsurer, setUploadInsurer] = useState('');
  // 3d/decision 7: the month the statement covers. Required before an upload,
  // and it fills `statement_date` for every row that carries none.
  const [uploadPeriod, setUploadPeriod] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  // #436: which layout to read the file as ('' = detect from its headers).
  const [uploadFormat, setUploadFormat] = useState('');
  // #436: tick to knowingly re-send a file already uploaded (rows already on
  // file are skipped, nothing is counted twice).
  const [uploadAgain, setUploadAgain] = useState(false);
  // B12: statement date or date range. Both bounds inclusive; a single day is
  // the same date in both boxes.
  const [dateAfter, setDateAfter] = useState('');
  const [dateBefore, setDateBefore] = useState('');
  // 3e: the line whose correction history is open. Held as the row itself, so
  // the drawer can show where the line stands now without waiting on a fetch.
  const [historyLine, setHistoryLine] = useState<StatementLine | null>(null);
  // STMT: which statement the difference report is showing. Set by a
  // successful upload (to that upload's insurer + period) or by its picker.
  const [reportQuery, setReportQuery] = useState<DifferenceReportQuery | null>(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [downloading, setDownloading] = useState<ReconciliationExportFormat | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 3b: insurer + dates — what the KPI cards are counted over. The status is
  // NOT in here: the cards are the status filter, so counting them through it
  // would collapse four of the five to zero. The server strips it too, so a
  // stale tab cannot produce a self-erasing card either.
  const scopeFilters = useMemo<ReconciliationFilters>(() => {
    const f: ReconciliationFilters = {};
    if (insurerFilter) f.insurer = insurerFilter;
    if (dateAfter) f.statement_date_after = dateAfter;
    if (dateBefore) f.statement_date_before = dateBefore;
    return f;
  }, [insurerFilter, dateAfter, dateBefore]);

  // ONE filter object feeds the table and the download, so the two can never
  // describe different rows. It is the scope above plus the chosen status.
  const filters = useMemo<ReconciliationFilters>(() => {
    const f: ReconciliationFilters = { ...scopeFilters };
    if (statusFilter === AWAITING_FILTER) {
      f.awaiting_policy = 'true';
    } else if (statusFilter === 'unmatched') {
      // "No matching policy" = unmatched lines that will NOT auto-match.
      f.status = 'unmatched';
      f.awaiting_policy = 'false';
    } else if (statusFilter) {
      f.status = statusFilter;
    }
    return f;
  }, [scopeFilters, statusFilter]);
  const rangeInverted = Boolean(dateAfter && dateBefore && dateAfter > dateBefore);
  const datesActive = Boolean(dateAfter || dateBefore);
  const filtersActive = Boolean(datesActive || statusFilter || insurerFilter);

  const user = useAuthStore((s) => s.user);
  const isBroker =
    user?.tenant_type === 'broker' ||
    user?.role === 'broker_admin' ||
    user?.role === 'broker_employee';
  // A platform admin may upload for ANY insurer, so the backend requires them
  // to say which — and answered a 400 when they could not, because the picker
  // rendered for brokers only.
  const isPlatformAdmin = user?.role === 'super_admin' || user?.role === 'country_admin';
  const mustChooseInsurer = isBroker || isPlatformAdmin;

  const linesQuery = useReconciliationLines(page, filters);
  const summaryQuery = useFilteredReconciliationSummary(scopeFilters);
  const upload = useUploadStatement();
  const rematch = useRematchPending();
  // Brokers and platform admins pick an insurer; an insurer user's upload is
  // hard-pinned to their own tenant server-side, so the field is not rendered
  // for them.
  const insurersQuery = useInsurersLookup();

  const lines = linesQuery.data?.results ?? [];
  const totalItems = linesQuery.data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;
  const summary = summaryQuery.data;
  const net = netDifference(summary);
  // M-02: `counts.unmatched` includes the lines awaiting their policy; the two
  // cards split it so each number means one thing.
  const awaitingCount = summary?.awaiting_policy ?? 0;
  const noMatchCount = summary ? Math.max(summary.counts.unmatched - awaitingCount, 0) : undefined;

  /**
   * 3b: one card's count and net difference. Served under `buckets`; an older
   * API (or a cached response from one) has only `counts`, so fall back to
   * those rather than showing five dashes — the count is the number people act
   * on, and a missing net difference renders as "—", not as zero.
   */
  const cardFigures = (bucket: KpiBucketKey): { count?: number; net: number | null } => {
    const served = summary?.buckets?.[bucket];
    if (served) return { count: served.count, net: netDifference({ total_delta: served.total_delta }) };
    if (!summary) return { count: undefined, net: null };
    const legacy: Record<KpiBucketKey, number> = {
      reconciled: summary.counts.reconciled,
      disputed: summary.counts.disputed,
      awaiting_policy: awaitingCount,
      no_matching_policy: noMatchCount ?? 0,
      all:
        summary.counts.reconciled +
        summary.counts.disputed +
        summary.counts.unmatched +
        summary.counts.pending,
    };
    return { count: legacy[bucket], net: bucket === 'all' ? net : null };
  };

  const insurerOptions = [
    { value: '', label: 'Select insurance company…' },
    ...(insurersQuery.data ?? []).map((i) => ({ value: i.id, label: i.name })),
  ];
  // 3c: the filter's own list. Its blank entry means "every company", which is
  // the opposite of the upload picker's "you have not chosen one yet" — same
  // options, different empty meaning, so they do not share an array.
  const insurerFilterOptions = [
    { value: '', label: 'All Insurance Companies' },
    ...(insurersQuery.data ?? []).map((i) => ({ value: i.id, label: i.name })),
  ];

  // The backend REJECTS an upload without an insurer from anyone who could be
  // uploading for more than one (the statement is meaningless without knowing
  // who sent it), and it used to do so as a 400 the user read as "Request
  // failed with status code 400". Block the click here instead of letting them
  // discover it after picking a file.
  const insurerMissing = mustChooseInsurer && !uploadInsurer;
  // 3d: Submit stays disabled until the insurer (where one is asked for), the
  // period and the file are all set — the three things the row asks for.
  const uploadReady = !insurerMissing && Boolean(uploadPeriod) && Boolean(uploadFile);

  // Rows the importer refused — from a partly-successful upload (200) or from
  // one where nothing imported at all (400). Rendering these is the whole of
  // item 12: the endpoint has always reported them and this screen has never
  // shown them, so a wholly rejected statement looked like a clean run over an
  // empty file.
  const rowErrors = upload.isPending ? [] : uploadRowErrors(upload.data, upload.error);
  const uploadFailed = !upload.isPending && upload.isError;
  // `skipped`/`rechecked` now live on `UploadResult` itself (see
  // services/reconciliation.ts) rather than only on statementUpload's
  // `UploadResultWithSkips` stand-in, so no cast is needed here any more.
  const skippedCount = upload.isPending ? 0 : uploadSkippedCount(upload.data);
  const recheckedCount = upload.isPending ? 0 : uploadRecheckedCount(upload.data);
  // 3e: rows that corrected a line already on file. Said out loud for the same
  // reason as the skipped ones -- an upload that CHANGES a figure a broker has
  // already read must never be quieter than one that adds to it.
  const updatedCount = upload.isPending ? 0 : uploadUpdatedCount(upload.data);

  /**
   * 3d: picking the file no longer uploads it. The row asks for three things
   * and Submit sends them together — choosing the file first and the period
   * afterwards used to be impossible, because the file `change` event fired
   * the upload straight away.
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadFile(e.target.files?.[0] ?? null);
  };

  const handleSubmitUpload = () => {
    if (!uploadFile || !uploadReady) return;
    upload.mutate(
      {
        file: uploadFile,
        insurer: uploadInsurer || undefined,
        statementPeriod: uploadPeriod,
        format: uploadFormat || undefined,
        uploadAgain,
      },
      {
        // STMT: open the difference report on the statement just uploaded.
        onSuccess: (data) => {
          if (data.difference_report_query) setReportQuery(data.difference_report_query);
        },
        // Clear the chosen file once it is in, so a second click of Submit
        // cannot re-send the same statement by accident. The period and the
        // insurer stay: the next file is usually the same month's from the
        // same company.
        onSettled: () => {
          setUploadFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        },
      },
    );
  };

  /** Download the blank statement template. */
  const handleDownloadTemplate = async () => {
    try {
      const blob = await reconciliationService.downloadStatementTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'insurer_statement_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // The response body of a failed blob request IS a Blob, so
      // `getErrorMessage` cannot read the server's message out of it and would
      // fall through to axios's own "Request failed with status code 4xx" —
      // exactly the string Task A3 exists to stop showing people. Say something
      // plain instead, as the broker-rates template download already does.
      toast.error('Could not download the statement template.');
    }
  };

  const handleStatusFilterChange = (v: string) => {
    setStatusFilter(v);
    setPage(1);
  };

  /** 3b: a second click on the card you are already filtered to clears it. */
  const handleCardClick = (filterValue: string) => {
    handleStatusFilterChange(statusFilter === filterValue ? '' : filterValue);
  };

  const handleInsurerFilterChange = (v: string) => {
    setInsurerFilter(v);
    setPage(1);
  };

  const handleDateChange = (which: 'after' | 'before', value: string) => {
    if (which === 'after') setDateAfter(value);
    else setDateBefore(value);
    setPage(1);
  };

  const clearDates = () => {
    setDateAfter('');
    setDateBefore('');
    setPage(1);
  };

  /**
   * B12 "Download Reconciliation": exactly the rows the current filters show,
   * as CSV or Excel. Read-only on the server — downloading never re-imports or
   * re-reconciles anything.
   */
  const handleDownload = async (format: ReconciliationExportFormat) => {
    setDownloadMenuOpen(false);
    setDownloading(format);
    try {
      const blob = await commissionService.downloadReconciliation(format, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = reconciliationFileName(format, filters);
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // A failed blob request's body is a Blob, so getErrorMessage cannot read it.
      toast.error('Could not download the reconciliation. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      {/* What this tab compares — stated once, so the Difference column's sign
          and colour need no further explanation. */}
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Commission <span className="font-medium">received from the insurance company</span> against
        the <span className="font-medium">auto-approved commission</span> from Calculations.{' '}
        <span className="text-green-600 dark:text-green-400 font-medium">Green</span> means the
        insurer paid more than calculated;{' '}
        <span className="text-red-600 dark:text-red-400 font-medium">red</span> means a shortfall.
        {datesActive && ' The figures below cover the selected statement dates only.'}
      </p>

      {/* Summary strip. `summaryQuery.isError` is called out explicitly rather
          than falling through to the same '—' placeholder loading uses —
          "still loading" and "failed to load" are different facts, and only
          one of them means "try again". */}
      <div
        role="group"
        aria-label="Filter by status"
        className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-3 lg:grid-cols-5"
      >
        {KPI_CARDS.map((card) => {
          const figures = cardFigures(card.bucket);
          const failed = summaryQuery.isError;
          const netTone = TONE_CLASS[differenceTone(figures.net)];
          // The Net Difference card leads with the money and needs no second
          // figure; the four status cards lead with the count and carry their
          // own net difference underneath, so each says how much it is out by.
          const isNetCard = card.bucket === 'all';
          const received =
            summary?.total_stated != null
              ? `on ${formatCurrency(summary.total_stated)} received`
              : card.subLabel;

          return (
            <SummaryCard
              key={card.bucket}
              label={card.label}
              value={
                failed ? 'Unavailable' : isNetCard ? formatSignedAmount(figures.net) : figures.count ?? '—'
              }
              color={failed ? TONE_CLASS.unknown : isNetCard ? netTone : card.color}
              subLabel={
                isNetCard
                  ? failed
                    ? 'Could not load the summary'
                    : received
                  : card.subLabel
              }
              amount={failed || isNetCard ? undefined : formatSignedAmount(figures.net)}
              amountColor={netTone}
              selected={statusFilter === card.filterValue}
              onSelect={() => handleCardClick(card.filterValue)}
            />
          );
        })}
      </div>

      {/* 3d: the upload row — Insurance Company, Statement Period, File,
          Submit, on one line, with the template beside it. It is its own card,
          away from the filters: the two used to share one wrapping card, which
          is how "which insurer is this?" and "which insurer am I looking at?"
          became the same question. */}
      <Card className="mb-4">
        <fieldset>
          <legend className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Upload a statement
          </legend>
          <div className="flex flex-wrap items-end gap-3">
            {/* Insurer field — shown to everyone who may upload for more than
                one insurer: a broker, and a country/super admin. A picker, not
                free text: the old "Insurer code or ID" box meant a typo came
                back as "insurer not found" and a blank one as a 400. */}
            {mustChooseInsurer && (
              <div className="w-full sm:w-72">
                <Select
                  name="statement-insurer"
                  label="Insurance Company"
                  options={insurerOptions}
                  value={uploadInsurer}
                  onChange={(e) => setUploadInsurer(e.target.value)}
                  helperText="Select the insurance company this statement is from."
                />
              </div>
            )}

            {/* Decision 7. A month, not a day: an insurer's statement covers a
                period, and this is what every row with no date of its own is
                filed under. Required — the browser's own `required` marker is
                inside the label, so query it by regex, never by exact text.

                `type="month"` is a Chrome/Edge control; Firefox and desktop
                Safari render a PLAIN TEXT BOX. Since Upload is gated on this
                field, a broker on either of those would otherwise be stopped at
                an empty box with nothing saying what to type — hence the
                placeholder, the pattern and the example in the helper text. */}
            <div className="w-full sm:w-44">
              <label
                htmlFor="statement-period"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Statement Period
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </label>
              <input
                id="statement-period"
                name="statement-period"
                type="month"
                required
                placeholder="YYYY-MM"
                pattern="\d{4}-\d{2}"
                value={uploadPeriod}
                onChange={(e) => setUploadPeriod(e.target.value)}
                className={dateInputClass}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The month this statement covers, e.g. 2026-09.
              </p>
            </div>

            <div className="w-full sm:w-64">
              <label
                htmlFor="statement-format"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Format
              </label>
              <select
                id="statement-format"
                name="statement-format"
                value={uploadFormat}
                onChange={(e) => setUploadFormat(e.target.value)}
                className={dateInputClass}
              >
                {STATEMENT_FORMAT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={uploadAgain}
                  onChange={(e) => setUploadAgain(e.target.checked)}
                />
                Upload again (this file was sent before)
              </label>
            </div>

            {/* The backend reads statements through `tabular.read_rows`, which
                takes Excel as well as CSV — this `accept` mirrors
                TABULAR_EXTENSIONS so the picker does not hide the workbook the
                insurer actually sent. */}
            <div className="w-full sm:w-72">
              <label
                htmlFor="statement-file"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Statement File
                <span className="text-red-500 dark:text-red-400 ml-1">*</span>
              </label>
              <input
                ref={fileInputRef}
                id="statement-file"
                name="statement-file"
                type="file"
                accept={STATEMENT_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100 dark:file:bg-primary-900/40 dark:file:text-primary-200 dark:hover:file:bg-primary-900/60"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                CSV or Excel (.xlsb too). Needs a policy number and the amount paid, or a Digit / Intermediary Incentive statement as the insurer sent it.
              </p>
            </div>

            <div className="flex items-center gap-3 pb-6">
              <Button
                onClick={handleSubmitUpload}
                isLoading={upload.isPending}
                disabled={upload.isPending || !uploadReady}
              >
                Upload Statement
              </Button>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                Download Template
              </Button>
            </div>
          </div>
        </fieldset>
      </Card>

      {/* STMT: what the statement says against our books. Brokers and
          platform admins only -- the endpoint compares against a broker's
          books and refuses insurer logins. */}
      {mustChooseInsurer && (
        <StatementDifferenceReport
          query={reportQuery}
          onQueryChange={setReportQuery}
          insurerOptions={insurerOptions}
        />
      )}

      {/* 3c: the filters. Nothing here changes what the next upload is mapped
          to — narrowing the view to one insurer must never re-aim a statement
          at it. */}
      <Card className="mb-6">
        <fieldset>
          <legend className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Filter statement lines
          </legend>
          <div className="flex flex-wrap items-end gap-4">
            <div className="max-w-xs flex-1 min-w-[160px]">
              <Select
                name="reconciliation-status"
                label="Status"
                options={statusFilterOptions}
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              />
            </div>

            {/* 3c: filter by the company whose statement a line came from.
                Separate state from the upload picker above. */}
            <div className="max-w-xs flex-1 min-w-[200px]">
              <Select
                name="reconciliation-insurer"
                label="Insurance Company"
                options={insurerFilterOptions}
                value={insurerFilter}
                onChange={(e) => handleInsurerFilterChange(e.target.value)}
              />
            </div>

            {/* B12: statement date or range. Inclusive on both ends. */}
            <div className="min-w-[150px]">
              <label
                htmlFor="recon-statement-date-after"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Statement Date From
              </label>
              <input
                id="recon-statement-date-after"
                type="date"
                value={dateAfter}
                max={dateBefore || undefined}
                onChange={(e) => handleDateChange('after', e.target.value)}
                className={dateInputClass}
              />
            </div>
            <div className="min-w-[150px]">
              <label
                htmlFor="recon-statement-date-before"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Statement Date To
              </label>
              <input
                id="recon-statement-date-before"
                type="date"
                value={dateBefore}
                min={dateAfter || undefined}
                onChange={(e) => handleDateChange('before', e.target.value)}
                className={dateInputClass}
              />
            </div>
            {datesActive && (
              <Button variant="outline" size="sm" onClick={clearDates}>
                Clear dates
              </Button>
            )}
            {rangeInverted && (
              <p className="w-full text-xs text-red-600 dark:text-red-400">
                "Statement Date From" is after "Statement Date To", so no lines can match.
              </p>
            )}

            <div className="ml-auto flex flex-wrap items-center gap-3">
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setDownloadMenuOpen((open) => !open)}
                  isLoading={downloading !== null}
                  disabled={downloading !== null}
                  aria-haspopup="menu"
                  aria-expanded={downloadMenuOpen}
                >
                  Download Reconciliation
                </Button>
                {downloadMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      onClick={() => handleDownload('csv')}
                    >
                      CSV (.csv)
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                      onClick={() => handleDownload('xlsx')}
                    >
                      Excel (.xlsx)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </fieldset>
      </Card>

      {/* M-02: lines waiting for their policy. They settle by themselves when
          the policy is issued and its commission calculated; the button is
          for a broker who has just fixed a policy number and won't wait.
          Brokers only -- the endpoint re-matches the caller's own broker. */}
      {isBroker && awaitingCount > 0 && (
        <Card className="mb-6 border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="text-sm text-amber-800 dark:text-amber-300">
              <p className="font-medium">
                {awaitingCount} statement line{awaitingCount === 1 ? ' is' : 's are'} pending — the
                policy has not been issued yet.
              </p>
              <p className="mt-1 text-amber-700 dark:text-amber-400">
                {awaitingCount === 1 ? 'It is' : 'They are'} matched automatically once the policy is
                issued and its commission is calculated.
              </p>
            </div>
            <div className="flex gap-2">
              {statusFilter !== AWAITING_FILTER && (
                <Button variant="outline" size="sm" onClick={() => handleStatusFilterChange(AWAITING_FILTER)}>
                  Show pending
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => rematch.mutate(undefined)}
                isLoading={rematch.isPending}
                disabled={rematch.isPending}
              >
                Re-check now
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* B12: rows skipped because they were already imported. Said out loud —
          "I uploaded 40 rows and 12 appeared" must explain itself. */}
      {(skippedCount > 0 || recheckedCount > 0 || updatedCount > 0) && (
        <Card className="mb-6 border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
          <div className="flex items-start justify-between gap-4">
            <div className="text-sm text-blue-800 dark:text-blue-300">
              {skippedCount > 0 && (
                <p>
                  {skippedCount} row{skippedCount === 1 ? ' was' : 's were'} already imported and
                  {skippedCount === 1 ? ' was' : ' were'} skipped — no duplicate entries were created.
                </p>
              )}
              {/* An already-imported row whose line was still unmatched or
                  mismatched is re-checked, not skipped: re-uploading is how a
                  broker re-reconciles after adding the policy or fixing a rate. */}
              {recheckedCount > 0 && (
                <p>
                  {recheckedCount} already-imported row{recheckedCount === 1 ? ' was' : 's were'} re-checked
                  against the current policies and commission — no duplicate entries were created.
                </p>
              )}
              {/* 3e: the insurer restated an amount, so the line it already had
                  was corrected rather than duplicated. "History" on the row
                  shows what it was before. */}
              {updatedCount > 0 && (
                <p>
                  {updatedCount} row{updatedCount === 1 ? ' stated' : 's stated'} a different amount and
                  updated {updatedCount === 1 ? 'its' : 'their'} existing line in place — the difference
                  was recalculated and the change recorded in the line's history.
                </p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => upload.reset()}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}

      {/* Rejected rows. The upload endpoint has always returned `errors`; this
          screen never rendered them, so a statement in which every row failed
          produced "0 reconciled, 0 disputed, 0 unmatched" — the same words a
          successful upload of an empty file produced. Never hide these. */}
      {(rowErrors.length > 0 || uploadFailed) && (
        <Card className="mb-6 border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                {rowErrors.length > 0
                  ? `${rowErrors.length} row${rowErrors.length === 1 ? '' : 's'} could not be imported`
                  : 'The statement could not be uploaded'}
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                {uploadFailed
                  ? getErrorMessage(upload.error)
                  : 'Everything else in the file was imported. Fix these rows and upload them again.'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => upload.reset()}>
              Dismiss
            </Button>
          </div>
          {rowErrors.length > 0 && (
            <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto text-sm text-red-700 dark:text-red-400">
              {rowErrors.slice(0, 50).map((rowError, i) => (
                <li key={`${rowError.row ?? 'x'}-${i}`} className="font-mono text-xs">
                  {describeRowError(rowError)}
                </li>
              ))}
              {rowErrors.length > 50 && (
                <li className="text-xs italic">
                  …and {rowErrors.length - 50} more row(s) with the same kind of problem.
                </li>
              )}
            </ul>
          )}
        </Card>
      )}

      {/* Comparison table. A failed request must never fall into the
          `lines.length === 0` branch below — that reads as "you have nothing
          to reconcile", which is a different claim from "we could not check".
          This screen has shipped that exact mistake before. */}
      <Card padding="none">
        {linesQuery.isLoading ? (
          <TableSkeleton cols={7} />
        ) : linesQuery.isError ? (
          <ErrorState
            message={getErrorMessage(linesQuery.error)}
            onRetry={() => linesQuery.refetch()}
          />
        ) : lines.length === 0 ? (
          <EmptyState
            message={
              filtersActive
                ? 'No statement lines match these filters.'
                : 'Nothing to compare yet. Upload an insurer statement (CSV or Excel) to see it against your calculated commission.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {[
                      { label: 'Policy Number' },
                      // 3c: which company's statement this line came from. Two
                      // insurers' lines used to read as one undifferentiated
                      // list even though the FK was on every row.
                      { label: 'Insurance Company' },
                      { label: 'Received', hint: 'From insurer' },
                      { label: 'Calculated', hint: 'Auto-approved' },
                      { label: 'Difference', hint: 'Received − Calculated' },
                      { label: 'Status' },
                      { label: 'Statement Date' },
                    ].map((h) => (
                      <th
                        key={h.label}
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                      >
                        {h.label}
                        {h.hint && (
                          <span className="block mt-0.5 text-[10px] font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                            {h.hint}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {lines.map((line) => {
                    const difference = lineDifference(line);
                    const shown = lineStatusDisplay(line);
                    return (
                      <tr
                        key={line.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {line.policy_number}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {line.insurer_name ? (
                            // U-05: the company's logo beside its name.
                            <span className="flex items-center gap-2">
                              <InsurerLogo src={line.insurer_logo_url} name={line.insurer_name} size="xs" />
                              <span>{line.insurer_name}</span>
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(line.stated_amount)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {line.expected_amount != null
                            ? formatCurrency(line.expected_amount)
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span
                            className={`font-medium ${TONE_CLASS[differenceTone(difference)]}`}
                          >
                            {formatSignedAmount(difference)}
                          </span>
                          {/* 3e: a corrected line's figure has moved since it
                              was first uploaded. The button is rendered ONLY
                              when there is something to show, so its presence
                              is itself the signal that this number changed. */}
                          {(line.revision_count ?? 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => setHistoryLine(line)}
                              className="mt-1 block text-xs font-normal text-primary-600 underline-offset-2 hover:underline dark:text-primary-400"
                            >
                              History ({line.revision_count})
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={shown.variant} size="sm">
                            {shown.label}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(line.statement_date)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={20}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {/* 3e: how the difference on the row above was reached. */}
      <StatementLineHistoryDrawer
        line={historyLine}
        isOpen={historyLine !== null}
        onClose={() => setHistoryLine(null)}
      />
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * 3b: a KPI card IS a filter button, not a read-out beside one. `aria-pressed`
 * is the whole of "visibly selected" for a screen reader, so it is on the
 * button itself rather than implied by the ring.
 */
function SummaryCard({
  label,
  value,
  color,
  subLabel,
  amount,
  amountColor,
  selected,
  onSelect,
}: {
  label: string;
  value: number | string;
  color: string;
  subLabel?: string;
  /** The bucket's own net difference, under the count. Omitted on the card
   *  whose headline is already the net difference. */
  amount?: string;
  amountColor?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Card
      padding="none"
      className={cn(
        'h-full overflow-hidden',
        selected && 'ring-2 ring-primary-500 dark:ring-primary-400 border-primary-500 dark:border-primary-400',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="h-full w-full rounded-xl px-4 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
        {amount && (
          <p className={`mt-0.5 text-sm font-medium ${amountColor ?? ''}`}>{amount}</p>
        )}
        {subLabel && (
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            {subLabel}
          </p>
        )}
      </button>
    </Card>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-800" />
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-4"
        >
          {[...Array(cols)].map((_, j) => (
            <div
              key={j}
              className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-red-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
        Could not load statement lines
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{message}</p>
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
        This is not the same as there being nothing to reconcile — please try again.
      </p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {message}
      </h3>
    </div>
  );
}
